import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Card } from 'react-bootstrap';
import { Icon } from '../../common/components/Utilities';
import DataTable from '../../common/components/DataTable';
import ShoutoutsResource from '../../common/resource/ShoutoutsResource';
import PeopleLayout from '../layout/PeopleLayout';

function Dashboard(props) {
  const header = <DashboardHeader {...props} />;
  return <PeopleLayout {...props} header={header} content={<PeopleDashboard {...props} />} />;
}

function PeopleDashboard({ activeUser }) {
  return (
    <div className="row">
      <div className="col-8 dashboard-content">
        <Card>
          <Card.Body>
            <div className="row">
              <Shortcut
                href="https://gear4music.sharepoint.com/sites/Gear4musicIntranetPortal"
                icon="info-circle"
                label="Gear4music Intranet"
                large={false}
                primary
              />

              <Shortcut
                href="https://gear4music.sharepoint.com/sites/Gear4musicIntranetPortal/SitePages/Learning-&-Development-Hub.aspx"
                icon="graduation-cap"
                label="Learning &amp; Development Hub"
                large={false}
                primary
              />

              <Shortcut href={`/users/${activeUser.id}/objectives`} icon="tasks" label="My Objectives" />
              <Shortcut href={`/users/${activeUser.id}/sickness`} icon="clinic-medical" label="My Sickness" />

              <Shortcut href={`/users/${activeUser.id}/leave`} icon="plane-departure" label="My Leave" />
              <Shortcut href="/calendar" icon="calendar" label="Leave Calendar" />

              <Shortcut href="/squads" icon="users" label="Squads" />
              <Shortcut href="/shoutouts" icon="megaphone" label="Shoutouts" />
            </div>
          </Card.Body>
        </Card>
      </div>

      <div className="col-4 dashboard-sidebar">
        <TopShoutouts />
        <ValuesStuff />
        <DataTable
          url="/resource/vacancy"
          title="Open Vacancies"
          columns={['Role', 'Squad']}
          renderRow={(vacancy) => (
            <tr>
              <td><strong>{vacancy.role.title}</strong></td>
              <td>{vacancy.squad.name}</td>
            </tr>
          )}
        />
      </div>
    </div>
  );
}

function computeTopShoutouts(shoutouts) {
  const start = moment().startOf('month');
  const end = moment().endOf('month');

  const monthShoutouts = shoutouts.filter(
    (s) => moment(s.created_at).isBetween(start, end, null, '[]'),
  );

  const givenCounts = {};
  const receivedCounts = {};

  monthShoutouts.forEach((s) => {
    const authorId = s.author.id;
    const userId = s.user.id;
    if (!givenCounts[authorId]) givenCounts[authorId] = { user: s.author, count: 0 };
    givenCounts[authorId].count += 1;
    if (!receivedCounts[userId]) receivedCounts[userId] = { user: s.user, count: 0 };
    receivedCounts[userId].count += 1;
  });

  const topGiven = Object.values(givenCounts).sort((a, b) => b.count - a.count)[0] ?? null;
  const topReceived = Object.values(receivedCounts).sort((a, b) => b.count - a.count)[0] ?? null;

  if (!topGiven || !topReceived) return null;

  return { given: topGiven, received: topReceived };
}

function TopShoutouts() {
  const [shoutouts, setShoutouts] = useState(null);

  const load = async () => {
    try {
      const data = await ShoutoutsResource.index();
      setShoutouts(computeTopShoutouts(data));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="row mb-4">
      {shoutouts && (
        <>
          <StatBox title="Shoutouts given" value={shoutouts.given.count} context={shoutouts.given.user.name} icon="megaphone" />
          <StatBox title="Shoutouts received" value={shoutouts.received.count} context={shoutouts.received.user.name} icon="party-horn" />
        </>
      )}
    </div>
  );
}

function ValuesStuff() {
  return (
    <div className="row mb-12">
      <div className="col-12 col-md-12 mb-2">
        <div className="card card-dashboard-values">
          <div className="card-header">Our Values</div>
          <div className="card-body">
            <ul className="list-unstyled">
              <li>
                <i className="fa-solid fa-bullseye-arrow fa-xl" />
                Commitment to PURPOSE
              </li>
              <li>
                <i className="fa-solid fa-rocket fa-xl" />
                Courageous AMBITION
              </li>
              <li>
                <i className="fa-solid fa-pencil fa-xl" />
                Strive for SIMPLICITY
              </li>
              <li>
                <i className="fa-solid fa-lightbulb fa-xl" />
                Be PRAGMATIC
              </li>
              <li>
                <i className="fa-solid fa-puzzle-piece fa-xl" />
                Be a GOOD CITIZEN
              </li>
              <li>
                <i className="fa-solid fa-comment fa-xl" />
                Thoughtful SINCERITY
              </li>
            </ul>
            <a className="btn btn-success" href="https://docs.google.com/document/d/1hPTByahdECtmy_hMj6NEzjx4U0Q175IBTBp8CLtlyIs" target="_blank" rel="noreferrer">Some more info here!</a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Shortcut({
  href, icon, label, primary = false, large = true,
}) {
  return (
    <div className="col-12 col-md-6">
      {
        primary ? (
          <a
            target="_blank"
            rel="noreferrer"
            className={`btn-block btn btn-info ${large ? 'p-4 my-2' : 'p-2 my-2'}`}
            href={href}
          >
            <Icon variant="fad" icon={icon} size={large ? '4x' : '3x'} append="mb-4" />
            <div style={{ fontSize: large ? '1.6em' : '1.3em' }}>{label}</div>
          </a>
        ) : (
          <a
            target="_self"
            className={`btn-block btn btn-outline-secondary ${large ? 'p-4 my-2' : 'p-2 my-2'}`}
            href={href}
          >
            <Icon variant="fad" icon={icon} size={large ? '4x' : '3x'} append="mb-4" />
            <div style={{ fontSize: large ? '1.6em' : '1.3em' }}>{label}</div>
          </a>
        )
      }
    </div>
  );
}

function DashboardHeader({ activeUser }) {
  return (
    <div className="feature-header">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <h1 style={{ marginBottom: 0 }}>
              Welcome back,
              {' '}
              <strong>{activeUser.first_name}</strong>
              !
            </h1>
          </div>
        </div>
      </div>
    </div>
  );
}

export function StatBox({
  title,
  value,
  icon,
  iconClassName,
  units = '',
  diff = null,
  max = null,
  context = null,
}) {
  let statContext = context;
  let statDiff = diff;

  if (statDiff !== null) {
    const diffIcon = statDiff >= 0 ? 'arrow-up' : 'arrow-down';
    const diffColor = statDiff >= 0 ? 'text-success' : 'text-danger';
    statContext = (
      <>
        <span className={`mr-1 ${diffColor}`} style={{ fontSize: '1rem' }}>
          <Icon icon={diffIcon} append={iconClassName} variant="fad" size="lg" />
          {' '}
          {statDiff}
          %
        </span>
        <span className="small text-muted"> from previous week.</span>
      </>
    );
  } else if (max !== null) {
    statDiff = (100 / max) * value;
    const diffColor = statDiff >= 25 ? 'text-success' : 'text-warning';
    statContext = (
      <>
        <span className={`mr-1 ${diffColor}`} style={{ fontSize: '1rem' }}>
          {Math.round(statDiff)}
          %
        </span>
        <span className="small text-muted">
          {' '}
          of
          {max}
          {' '}
          {units}
          .
        </span>
      </>
    );
  } else {
    statContext = <span className="small text-muted">{statContext}</span>;
  }

  return (
    <div className="col-12 col-md-6 mb-2">
      <div className="card card-stat">
        <div className="card-body">
          <div className="row">
            <div className="col-12">
              <h4 className="text-muted">{title}</h4>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <div className="stat my-2">
                {value}
                {' '}
                {units}
              </div>
            </div>

            <div className="col-auto col">
              <div className="icon">
                <Icon variant="fad" icon={icon} size="3x" fixed />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              {statContext}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
